import { useEffect } from 'react';
import { useLanguage, META } from '../context/LanguageContext';

const SITE_URL = 'https://spanischenlared.com';
const DEFAULT_IMAGE = `${SITE_URL}/logo.png`;

/**
 * Sets per-page SEO: <title>, <meta description>, canonical and social tags.
 * Las etiquetas se gestionan directamente sobre el <head> para que quede
 * SIEMPRE una sola de cada una (el index.html y el prerender ya traen las
 * suyas y antes quedaban duplicadas, con Google quedándose la genérica).
 */
export default function Seo({
  title,
  fullTitle,
  description,
  path = '/',
  image = DEFAULT_IMAGE,
  type = 'website',
  noindex = false,
  statusCode = null,
  hreflang = [],
}) {
  const { language } = useLanguage();
  const defaults = META[language] || META.es;
  const canonical = `${SITE_URL}${path === '/' ? '/' : path.replace(/\/+$/, '')}`;
  const finalTitle = fullTitle || (title ? `${title} · Spanisch en la Red` : defaults.title);
  const desc = description || defaults.description;

  useEffect(() => {
    const head = document.head;

    const single = (selector, create) => {
      const found = [...head.querySelectorAll(selector)];
      found.slice(1).forEach((el) => el.remove());
      const el = found[0] || create();
      if (!el.parentNode) head.appendChild(el);
      return el;
    };

    const meta = (attr, name, content) => {
      const el = single(`meta[${attr}="${name}"]`, () => {
        const m = document.createElement('meta');
        m.setAttribute(attr, name);
        return m;
      });
      el.setAttribute('content', content);
    };

    document.title = finalTitle;
    single('title', () => document.createElement('title')).textContent = finalTitle;

    meta('name', 'title', finalTitle);
    meta('name', 'description', desc);
    meta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow');
    if (statusCode) meta('name', 'prerender-status-code', String(statusCode));
    meta('property', 'og:type', type);
    meta('property', 'og:url', canonical);
    meta('property', 'og:title', finalTitle);
    meta('property', 'og:description', desc);
    meta('property', 'og:image', image);
    meta('name', 'twitter:card', 'summary_large_image');
    meta('name', 'twitter:title', finalTitle);
    meta('name', 'twitter:description', desc);
    meta('name', 'twitter:image', image);

    const links = [...head.querySelectorAll('link[rel="canonical"]')];
    links.slice(noindex ? 0 : 1).forEach((el) => el.remove());
    if (!noindex) {
      const link = links[0] || document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', canonical);
      if (!link.parentNode) head.appendChild(link);
    }

    // hreflang: solo entre versiones realmente equivalentes.
    head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    if (!noindex) {
      hreflang.forEach(({ lang, url }) => {
        const alt = document.createElement('link');
        alt.setAttribute('rel', 'alternate');
        alt.setAttribute('hreflang', lang);
        alt.setAttribute('href', url);
        head.appendChild(alt);
      });
    }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalTitle, desc, canonical, image, type, noindex, statusCode, JSON.stringify(hreflang)]);

  return null;
}
